/**
 * Mouse and Touch Interaction Module
 * Phase 5.2: Drag-to-rotate functionality (bounded to globe)
 * Phase 6: Updated for dual rendering - only globe is interactive
 * Mobile Support: Added touch event handlers for mobile devices
 *
 * Ported from Python: map_projection/app/earth_model_sprite.py lines 69-85
 */

import { emit } from '../core/event-bus.js';
import { isUIEvent } from './interaction-guard.js';

/**
 * Initialize mouse and touch drag handlers for rotating the earth
 * @param {Object} app - Application state from main.js
 * @param {Object} globeBounds - { x, y, width, height } for globe area
 * @returns {Function} cleanup - Function to remove event listeners
 */
export function initMouseHandler(app, globeBounds) {
    let isDragging = false;
    let lastMousePos = null;
    let lastTouchId = null; // Track which touch is controlling rotation

    // Mouse speed scaling (Python: C.mouse_speed_scaling = window_size / 200)
    // For 1000px window: scaling = 5.0
    const getMouseSpeedScaling = () => {
        return Math.min(window.innerWidth, window.innerHeight) / 200;
    };

    /**
     * Check if mouse position is within globe bounds
     */
    const isOverGlobe = (clientX, clientY) => {
        return (
            clientX >= globeBounds.x &&
            clientX <= globeBounds.x + globeBounds.width &&
            clientY >= globeBounds.y &&
            clientY <= globeBounds.y + globeBounds.height
        );
    };

    /**
     * Handle mouse down - start tracking drag
     */
    const onMouseDown = (event) => {
        if (isUIEvent(event)) return;
        // Only start drag if over globe area
        if (isOverGlobe(event.clientX, event.clientY)) {
            isDragging = true;
            lastMousePos = { x: event.clientX, y: event.clientY };

            // Change cursor
            document.body.style.cursor = 'grabbing';

            // Prevent default to avoid text selection
            event.preventDefault();
        }
    };

    /**
     * Handle mouse move - apply rotation if dragging
     */
    const onMouseMove = (event) => {
        // Update cursor based on position
        if (isOverGlobe(event.clientX, event.clientY)) {
            if (!isDragging) {
                document.body.style.cursor = 'grab';
            }
        } else {
            if (!isDragging) {
                document.body.style.cursor = 'default';
            }
        }

        if (!isDragging || !lastMousePos || !app.earthModel) {
            return;
        }

        // Calculate delta
        const dx = event.clientX - lastMousePos.x;
        const dy = event.clientY - lastMousePos.y;

        // Update last position
        lastMousePos = { x: event.clientX, y: event.clientY };

        // Apply scaling (Python: dx/dy divided by mouse_speed_scaling)
        const scaling = getMouseSpeedScaling();
        const scaledDx = dx / scaling;
        const scaledDy = dy / scaling;

        // Apply rotation (Python: rotate_from_state(0, dy, -dx))
        // Horizontal drag → gamma (X-axis), Vertical drag → beta (Y-axis)
        app.earthModel.rotateFromState(0, scaledDy, -scaledDx);

        // Re-render
        emit('render:dirty');
    };

    /**
     * Handle mouse up - stop tracking drag
     */
    const onMouseUp = (event) => {
        if (isDragging) {
            isDragging = false;
            lastMousePos = null;

            // Reset cursor based on current position
            document.body.style.cursor = isOverGlobe(event.clientX, event.clientY) ? 'grab' : 'default';
        }
    };

    /**
     * Handle mouse leaving window - stop drag
     */
    const onMouseLeave = () => {
        if (isDragging) {
            isDragging = false;
            lastMousePos = null;
            document.body.style.cursor = 'default';
        }
    };

    /**
     * Get position from touch event
     */
    const getTouchPosition = (touch) => {
        return { x: touch.clientX, y: touch.clientY };
    };

    /**
     * Handle touch start - start tracking drag
     */
    const onTouchStart = (event) => {
        if (event.touches.length === 1) {
            const touch = event.touches[0];

            // Don't hijack touches that start on interactive UI elements (buttons, etc.)
            if (isUIEvent(event)) return;

            // Only start drag if over globe area
            if (isOverGlobe(touch.clientX, touch.clientY)) {
                isDragging = true;
                lastMousePos = getTouchPosition(touch);
                lastTouchId = touch.identifier;

                // Prevent default to avoid scrolling
                event.preventDefault();
            }
        }
    };

    /**
     * Handle touch move - apply rotation if dragging
     */
    const onTouchMove = (event) => {
        if (!isDragging || !lastMousePos || !app.earthModel) {
            return;
        }

        // Find the touch we're tracking
        for (let i = 0; i < event.touches.length; i++) {
            const touch = event.touches[i];

            if (touch.identifier === lastTouchId) {
                // Calculate delta
                const dx = touch.clientX - lastMousePos.x;
                const dy = touch.clientY - lastMousePos.y;

                // Update last position
                lastMousePos = getTouchPosition(touch);

                // Apply scaling (same as mouse)
                const scaling = getMouseSpeedScaling();
                const scaledDx = dx / scaling;
                const scaledDy = dy / scaling;

                // Apply rotation
                app.earthModel.rotateFromState(0, scaledDy, -scaledDx);

                // Re-render
                emit('render:dirty');

                // Prevent default to avoid scrolling
                event.preventDefault();
                break;
            }
        }
    };

    /**
     * Handle touch end - stop tracking drag
     */
    const onTouchEnd = (event) => {
        // Check if our tracked touch ended
        if (isDragging) {
            let touchStillActive = false;
            for (let i = 0; i < event.touches.length; i++) {
                if (event.touches[i].identifier === lastTouchId) {
                    touchStillActive = true;
                    break;
                }
            }

            if (!touchStillActive) {
                isDragging = false;
                lastMousePos = null;
                lastTouchId = null;
            }
        }
    };

    /**
     * Handle touch cancel - stop tracking drag
     */
    const onTouchCancel = () => {
        if (isDragging) {
            isDragging = false;
            lastMousePos = null;
            lastTouchId = null;
        }
    };

    // Attach event listeners
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mouseleave', onMouseLeave);

    // Touch event listeners
    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchCancel);

    console.log('✓ Mouse and touch handlers initialized (globe bounds)');

    // Return cleanup function
    return () => {
        window.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('mouseleave', onMouseLeave);

        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('touchcancel', onTouchCancel);

        document.body.style.cursor = 'default';
    };
}
